import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { useNav } from "@/utils/nav";
import {
    Checkbox,
    CheckboxList,
    ComboBox,
    Date,
    DateTime,
    Email,
    FileInput,
    FirstName,
    Input,
    LastName,
    MonthYear,
    MultiSelect,
    MultipleChoiceCards,
    Number,
    Password,
    Phone,
    PIFU,
    SaveButton,
    Select,
    SelectTitle,
    Switch,
    Textarea,
    Time,
    YesNo,
    type OptionT,
} from "@patient-watch/forms";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { FormProvider, useForm } from "react-hook-form";

export const Route = createFileRoute("/_app/design/components/inputs")({
    component: InputsDemo,
});

const DEMO_OPTIONS: OptionT[] = [
    { value: "option-a", label: "Option A" },
    { value: "option-b", label: "Option B" },
    { value: "option-c", label: "Option C", description: "With description" },
];

interface DemoFormValues {
    text: string;
    textarea: string;
    email: string;
    password: string;
    phone: string;
    firstName: string;
    lastName: string;
    number: string;
    select: string;
    combobox: string;
    multiSelect: string[];
    checkboxList: string[];
    multipleChoiceCards: string[];
    checkbox: boolean;
    switch: boolean;
    yesNo: string;
    date: string;
    monthYear: string;
    dateTime: string;
    time: string;
    title: string;
    pifu: boolean;
    file: File | null;
}

function DemoSection({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="space-y-4 pt-2 border-t">
            <h2 className="font-semibold text-lg">{title}</h2>
            <div className="space-y-6">{children}</div>
        </section>
    );
}

function InputsDemo() {
    useNav([
        { label: "Design", route: { to: "/design" } },
        { label: "Form inputs", route: { to: "/design/components/inputs" } },
    ]);

    const form = useForm<DemoFormValues>({
        defaultValues: {
            text: "",
            textarea: "",
            email: "",
            password: "",
            phone: "",
            firstName: "",
            lastName: "",
            number: "",
            select: "",
            combobox: "",
            multiSelect: [],
            checkboxList: [],
            multipleChoiceCards: [],
            checkbox: false,
            switch: false,
            yesNo: "",
            date: "",
            monthYear: "",
            dateTime: "",
            time: "",
            title: "",
            pifu: false,
            file: null,
        },
    });

    const values = form.watch();

    return (
        <AdminPageLayout
            title="Form inputs"
            description="Shared field components from @patient-watch/forms. Values update live below the form."
            maxWidthClassName="max-w-2xl"
        >
            <div className="space-y-8">
                <FormProvider {...form}>
                    <form className="space-y-8" onSubmit={form.handleSubmit(() => undefined)}>
                        <DemoSection title="Text">
                            <Input name="text" label="Text input" placeholder="Enter some text" required />
                            <Textarea name="textarea" label="Textarea" placeholder="Longer text..." />
                            <Email name="email" label="Email" />
                            <Password name="password" label="Password" />
                            <Phone name="phone" label="Phone" locale="en-GB" />
                            <FirstName name="firstName" />
                            <LastName name="lastName" />
                        </DemoSection>

                        <DemoSection title="Numbers">
                            <Number name="number" label="Number" control={form.control} />
                        </DemoSection>

                        <DemoSection title="Select & choice">
                            <Select
                                name="select"
                                label="Select"
                                placeholder="Choose an option"
                                options={DEMO_OPTIONS}
                            />
                            <ComboBox
                                name="combobox"
                                label="ComboBox"
                                placeholder="Search options..."
                                options={DEMO_OPTIONS}
                                clearable
                            />
                            <MultiSelect
                                name="multiSelect"
                                label="MultiSelect"
                                options={DEMO_OPTIONS}
                                trigger={
                                    <ui.Button type="button" variant="outline">
                                        Choose multiple
                                    </ui.Button>
                                }
                            />
                            <CheckboxList name="checkboxList" label="Checkbox list" options={DEMO_OPTIONS} />
                            <MultipleChoiceCards
                                name="multipleChoiceCards"
                                label="Multiple choice cards"
                                options={["Card A", "Card B", "Card C"]}
                            />
                            <SelectTitle name="title" label="Title" />
                        </DemoSection>

                        <DemoSection title="Boolean">
                            <Checkbox name="checkbox" label="Checkbox" description="Toggle a boolean value." />
                            <Switch name="switch" label="Switch" description="Another boolean control." />
                            <YesNo name="yesNo" label="Yes / No" required />
                            <PIFU control={form.control} />
                        </DemoSection>

                        <DemoSection title="Date & time">
                            <Date name="date" label="Date" />
                            <MonthYear name="monthYear" label="Month / year" />
                            <DateTime name="dateTime" label="Date & time" />
                            <Time name="time" label="Time" />
                        </DemoSection>

                        <DemoSection title="Files">
                            <FileInput name="file" label="File upload" />
                        </DemoSection>

                        <SaveButton type="submit" />
                    </form>
                </FormProvider>

                <ui.Card>
                    <ui.CardHeader>
                        <ui.CardTitle>Live values</ui.CardTitle>
                        <ui.CardDescription>Current react-hook-form state for the demo fields.</ui.CardDescription>
                    </ui.CardHeader>
                    <ui.CardContent>
                        <pre className="bg-muted p-4 rounded-md overflow-x-auto text-sm">
                            {JSON.stringify(
                                {
                                    ...values,
                                    file: values.file ? values.file.name : null,
                                },
                                null,
                                2
                            )}
                        </pre>
                    </ui.CardContent>
                </ui.Card>
            </div>
        </AdminPageLayout>
    );
}
